'use client'

import { Link } from '@/shared/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  fetchKitchenStockLots,
  fetchKitchenWasteStockLots,
  type ApiStockItem,
} from '@/features/kitchen/api/getDispatchOptions'
import {
  fetchKitchenMaterialStock,
  type ApiMaterialStockItem,
} from '@/features/kitchen/api/kitchenTransferApi'
import { shouldUseKitchenApi } from '@/features/kitchen/api/kitchenApiMode'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonList } from '@/shared/components/Skeleton'
import { getKitchenDevice } from '@/shared/auth/deviceStorage'

type StockSource = 'main' | 'return'

type StockLot = {
  productName: string
  productionDate: string
  qty: number
  source: StockSource
}

type ProductStockGroup = {
  productName: string
  totalQty: number
  lots: StockLot[]
}

type SourceFilter = 'all' | 'main' | 'return'
type ViewTab = 'product' | 'material'

const MOCK_MATERIALS: ApiMaterialStockItem[] = [
  { materialName: 'Tepung Cakra Kembar', uom: 'kg', stockAvailable: 25 },
  { materialName: 'Gula pasir', uom: 'kg', stockAvailable: 15 },
  { materialName: 'Mentega Wysman', uom: 'kg', stockAvailable: 8 },
  { materialName: 'Susu UHT', uom: 'l', stockAvailable: 20 },
  { materialName: 'Ragi instan', uom: 'kg', stockAvailable: 3 },
  { materialName: 'Garam halus', uom: 'kg', stockAvailable: 5 },
  { materialName: 'Telur ayam', uom: 'kg', stockAvailable: 10 },
]

const MOCK_GOOD_LOTS: StockLot[] = [
  { productName: 'Roti coklat', productionDate: '27/06/26', qty: 70, source: 'main' },
  { productName: 'Roti coklat', productionDate: '26/06/26', qty: 30, source: 'main' },
  { productName: 'Roti keju', productionDate: '27/06/26', qty: 40, source: 'main' },
  { productName: 'Donat gula', productionDate: '27/06/26', qty: 10, source: 'main' },
  { productName: 'Croissant', productionDate: '27/06/26', qty: 12, source: 'main' },
]

const MOCK_WASTE_LOTS: StockLot[] = [
  { productName: 'Donat gula', productionDate: '26/06/26', qty: 15, source: 'return' },
  { productName: 'Roti coklat', productionDate: '25/06/26', qty: 8, source: 'return' },
  { productName: 'Croissant', productionDate: '25/06/26', qty: 6, source: 'return' },
]

function mapApiLots(items: ApiStockItem[], source: StockSource): StockLot[] {
  return items.map((item) => ({
    productName: item.productName,
    productionDate: item.productionDate,
    qty: item.stockAvailable,
    source,
  }))
}

function buildProductGroups(lots: StockLot[]): ProductStockGroup[] {
  const map = new Map<string, StockLot[]>()
  for (const lot of lots) {
    const list = map.get(lot.productName) ?? []
    list.push(lot)
    map.set(lot.productName, list)
  }
  return [...map.entries()]
    .map(([productName, productLots]) => ({
      productName,
      totalQty: productLots.reduce((sum, lot) => sum + lot.qty, 0),
      lots: productLots.sort((a, b) => b.productionDate.localeCompare(a.productionDate)),
    }))
    .sort((a, b) => a.productName.localeCompare(b.productName, 'id'))
}

function sourceLabel(source: StockSource): string {
  return source === 'main' ? kitchenTerms.waste.goodStock : kitchenTerms.waste.wasteStock
}

function sourceChipClass(source: StockSource): string {
  return source === 'main'
    ? 'bg-surface-container text-on-surface-variant border border-outline-variant'
    : 'bg-secondary-fixed text-on-secondary-fixed-variant border border-secondary-fixed-dim'
}

export function KitchenStockPage() {
  const device = getKitchenDevice()
  const useApi = shouldUseKitchenApi()

  const [lots, setLots] = useState<StockLot[]>([])
  const [materials, setMaterials] = useState<ApiMaterialStockItem[]>([])
  const [isLoading, setIsLoading] = useState(useApi)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [viewTab, setViewTab] = useState<ViewTab>('product')
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [refreshKey, setRefreshKey] = useState(0)

  const reload = useCallback(() => {
    if (!useApi) {
      setLots([...MOCK_GOOD_LOTS, ...MOCK_WASTE_LOTS])
      setMaterials(MOCK_MATERIALS)
      setIsLoading(false)
      setLoadError(null)
      return
    }
    if (!device?.deviceId) {
      setLots([])
      setMaterials([])
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setLoadError(null)

    Promise.all([
      fetchKitchenStockLots(device.deviceId),
      fetchKitchenWasteStockLots(device.deviceId),
      fetchKitchenMaterialStock(device.deviceId),
    ])
      .then(([good, waste, matStock]) => {
        if (cancelled) return
        setLots([...mapApiLots(good, 'main'), ...mapApiLots(waste, 'return')])
        setMaterials(matStock)
      })
      .catch(() => {
        if (!cancelled) {
          setLots([])
          setMaterials([])
          setLoadError('Gagal memuat stok. Periksa koneksi lalu coba lagi.')
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId])

  useEffect(() => {
    return reload()
  }, [reload, refreshKey])

  const filteredLots = useMemo(() => {
    const q = search.trim().toLowerCase()
    return lots.filter((lot) => {
      if (sourceFilter !== 'all' && lot.source !== sourceFilter) return false
      if (!q) return true
      return lot.productName.toLowerCase().includes(q)
    })
  }, [lots, search, sourceFilter])

  const groups = useMemo(() => buildProductGroups(filteredLots), [filteredLots])

  const filteredMaterials = useMemo(() => {
    const q = search.trim().toLowerCase()
    return materials
      .filter((item) => item.stockAvailable > 0 || !useApi)
      .filter((item) => !q || item.materialName.toLowerCase().includes(q))
      .sort((a, b) => a.materialName.localeCompare(b.materialName, 'id'))
  }, [materials, search, useApi])

  const summary = useMemo(() => {
    const goodQty = filteredLots.filter((l) => l.source === 'main').reduce((s, l) => s + l.qty, 0)
    const wasteQty = filteredLots.filter((l) => l.source === 'return').reduce((s, l) => s + l.qty, 0)
    const materialQty = filteredMaterials.reduce((s, m) => s + m.stockAvailable, 0)
    return {
      productCount: groups.length,
      dateLineCount: filteredLots.length,
      totalQty: goodQty + wasteQty,
      goodQty,
      wasteQty,
      materialCount: filteredMaterials.length,
      materialQty,
    }
  }, [filteredLots, groups.length, filteredMaterials])

  function toggleExpanded(productName: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(productName)) next.delete(productName)
      else next.add(productName)
      return next
    })
  }

  function expandAll() {
    setExpanded(new Set(groups.map((g) => g.productName)))
  }

  function collapseAll() {
    setExpanded(new Set())
  }

  const updatedLabel = new Intl.DateTimeFormat('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date())

  return (
    <section className="space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-headline-md text-primary">{kitchenTerms.modules.stock}</p>
          <p className="text-body-sm text-on-surface-variant mt-1">
            Stok real-time dari ledger dapur · diperbarui {updatedLabel}
          </p>
        </div>
        <button
          type="button"
          className="btn-outline shrink-0 min-w-[44px] px-3"
          onClick={() => setRefreshKey((k) => k + 1)}
          aria-label="Perbarui stok"
          title="Perbarui stok"
        >
          <span className="material-symbols-outlined">refresh</span>
        </button>
      </header>

      <div className="flex gap-2">
        {(
          [
            { key: 'product' as const, label: 'Produk jadi' },
            { key: 'material' as const, label: kitchenTerms.waste.materialStock },
          ] as const
        ).map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => {
              setViewTab(tab.key)
              setSearch('')
            }}
            className={`flex-1 rounded-lg px-3 py-2.5 text-body-sm font-semibold min-h-[44px] ${
              viewTab === tab.key
                ? 'bg-primary-container text-on-primary-container'
                : 'bg-surface text-on-surface-variant border border-outline-variant'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="card space-y-3">
        {viewTab === 'product' ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
          <div className="rounded-lg bg-surface-container p-2">
            <p className="text-label-md text-on-surface-variant">Produk</p>
            <p className="text-headline-md text-primary mt-0.5">{summary.productCount}</p>
          </div>
          <div className="rounded-lg bg-surface-container p-2">
            <p className="text-label-md text-on-surface-variant">Tanggal prod.</p>
            <p className="text-headline-md text-primary mt-0.5">{summary.dateLineCount}</p>
          </div>
          <div className="rounded-lg bg-surface-container p-2">
            <p className="text-label-md text-on-surface-variant">{kitchenTerms.waste.goodStock}</p>
            <p className="text-headline-md text-primary mt-0.5">{summary.goodQty}</p>
          </div>
          <div className="rounded-lg bg-surface-container p-2">
            <p className="text-label-md text-on-surface-variant">{kitchenTerms.waste.wasteStock}</p>
            <p className="text-headline-md text-primary mt-0.5">{summary.wasteQty}</p>
          </div>
        </div>
        ) : (
        <div className="grid grid-cols-2 gap-2 text-center">
          <div className="rounded-lg bg-surface-container p-2">
            <p className="text-label-md text-on-surface-variant">Jenis bahan</p>
            <p className="text-headline-md text-primary mt-0.5">{summary.materialCount}</p>
          </div>
          <div className="rounded-lg bg-surface-container p-2">
            <p className="text-label-md text-on-surface-variant">Total saldo</p>
            <p className="text-headline-md text-primary mt-0.5">{summary.materialQty}</p>
          </div>
        </div>
        )}

        <div className="relative">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant text-xl pointer-events-none">
            search
          </span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={viewTab === 'product' ? 'Cari nama produk...' : 'Cari bahan baku...'}
            className="input-field w-full pl-10"
            aria-label={viewTab === 'product' ? 'Cari produk' : 'Cari bahan baku'}
          />
        </div>

        {viewTab === 'product' && (
        <div className="flex flex-wrap gap-2">
          {(
            [
              { key: 'all' as const, label: 'Semua' },
              { key: 'main' as const, label: kitchenTerms.waste.goodStock },
              { key: 'return' as const, label: kitchenTerms.waste.wasteStock },
            ] as const
          ).map((filter) => (
            <button
              key={filter.key}
              type="button"
              onClick={() => setSourceFilter(filter.key)}
              className={`rounded-lg px-3 py-2 text-body-sm font-semibold min-h-[44px] ${
                sourceFilter === filter.key
                  ? 'bg-primary-container text-on-primary-container'
                  : 'bg-surface text-on-surface-variant border border-outline-variant'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
        )}

        {viewTab === 'product' && groups.length > 1 && (
          <div className="flex gap-2 text-body-sm">
            <button type="button" className="text-primary font-semibold" onClick={expandAll}>
              Buka semua
            </button>
            <span className="text-on-surface-variant">·</span>
            <button type="button" className="text-primary font-semibold" onClick={collapseAll}>
              Tutup semua
            </button>
          </div>
        )}
      </div>

      {isLoading && <SkeletonList count={8} label="Memuat stok dapur" />}

      {loadError && (
        <div role="alert" className="alert-error">
          <span className="material-symbols-outlined text-base shrink-0">error</span>
          <span>{loadError}</span>
        </div>
      )}

      {!isLoading && !loadError && viewTab === 'product' && groups.length === 0 && (
        <EmptyState
          icon="inventory_2"
          title="Tidak ada stok"
          description={
            search.trim()
              ? 'Tidak ada produk yang cocok dengan pencarian.'
              : 'Belum ada stok tercatat di dapur untuk filter ini.'
          }
        />
      )}

      {!isLoading && !loadError && viewTab === 'material' && filteredMaterials.length === 0 && (
        <EmptyState
          icon="grocery"
          title="Tidak ada stok bahan baku"
          description={
            search.trim()
              ? 'Tidak ada bahan yang cocok dengan pencarian.'
              : 'Belum ada saldo bahan baku di dapur ini.'
          }
        />
      )}

      {!isLoading && viewTab === 'product' && groups.length > 0 && (
        <div className="space-y-2">
          {groups.map((group) => {
            const isOpen = expanded.has(group.productName) || group.lots.length === 1
            const showToggle = group.lots.length > 1
            return (
              <div key={group.productName} className="card p-0 overflow-hidden">
                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-3 p-3 text-left min-h-[44px]"
                  onClick={() => showToggle && toggleExpanded(group.productName)}
                  disabled={!showToggle}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-body-lg font-bold text-on-surface truncate">{group.productName}</p>
                    <p className="text-label-md text-on-surface-variant mt-0.5">
                      {group.lots.length} tanggal produksi · {group.totalQty} pcs total
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-body-lg font-bold text-primary">{group.totalQty}</span>
                    {showToggle && (
                      <span className="material-symbols-outlined text-on-surface-variant">
                        {isOpen ? 'expand_less' : 'expand_more'}
                      </span>
                    )}
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-surface-variant divide-y divide-surface-variant">
                    {group.lots.map((lot) => (
                      <div
                        key={`${lot.productName}|${lot.productionDate}|${lot.source}`}
                        className="flex items-center justify-between gap-3 px-3 py-2.5"
                      >
                        <div className="min-w-0">
                          <span
                            className={`inline-flex text-[10px] font-semibold rounded-full px-2 py-0.5 ${sourceChipClass(lot.source)}`}
                          >
                            {sourceLabel(lot.source)}
                          </span>
                          <p className="text-body-sm text-on-surface-variant mt-1">
                            Tanggal produksi {lot.productionDate}
                          </p>
                        </div>
                        <p className="text-body-lg font-bold text-on-surface shrink-0">{lot.qty} pcs</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {!isLoading && viewTab === 'material' && filteredMaterials.length > 0 && (
        <div className="space-y-2">
          {filteredMaterials.map((item) => (
            <div key={`${item.materialName}-${item.uom}`} className="card flex items-center justify-between gap-3 p-3">
              <div className="min-w-0">
                <p className="text-body-lg font-bold text-on-surface truncate">{item.materialName}</p>
                <p className="text-label-md text-on-surface-variant mt-0.5">
                  {kitchenTerms.waste.materialStock} · {item.uom}
                </p>
              </div>
              <p className="text-body-lg font-bold text-primary shrink-0">
                {item.stockAvailable} {item.uom}
              </p>
            </div>
          ))}
        </div>
      )}

      <Link href="/kitchen/home" className="btn-outline block text-center">
        Kembali ke beranda
      </Link>
    </section>
  )
}
